/**
 * Arithmetic coder bindings.
 *
 * In production we want to import the WASM module built from `codec-rs/`:
 *
 *   import init, { encode, decode } from "codec-rs";
 *
 * Until `wasm-pack build --target web` has run and produced `codec-rs/pkg/`,
 * we ship a pure-JS fallback with the *same* numeric behavior so the demo
 * runs end-to-end. Both implementations use 32-bit fixed-point integer
 * arithmetic with a 14-bit precision frequency table (so they're
 * bit-compatible).
 *
 * Frequencies are 16-bit unsigned ints, scaled so they sum to TOTAL_FREQ.
 * The model's Float32 probabilities are quantized once per byte via
 * `probsToFreqs()` below; both encoder and decoder MUST use the identical
 * quantizer for the codec to round-trip.
 */

const PRECISION = 32;
const TOTAL_BITS = 14;
const TOTAL_FREQ = 1 << TOTAL_BITS; // 16384
const TOP = 1 << (PRECISION - 1); // 0x80000000
const SECOND = 1 << (PRECISION - 2); // 0x40000000
const MAX_RANGE = 0xffffffff;

/** Quantize a length-256 prob distribution to a length-256 freq table summing to TOTAL_FREQ. */
export function probsToFreqs(probs: Float32Array): Uint16Array {
  const freqs = new Uint16Array(256);
  // Floor each to at least 1 (no zero-mass symbols), then distribute leftover by largest fractional part.
  const scaled = new Float64Array(256);
  let floorSum = 0;
  for (let i = 0; i < 256; i++) {
    const s = probs[i] * (TOTAL_FREQ - 256); // reserve 1 each for the floor
    scaled[i] = s;
    freqs[i] = 1 + Math.floor(s);
    floorSum += freqs[i];
  }
  let leftover = TOTAL_FREQ - floorSum;
  if (leftover > 0) {
    // hand out remaining mass to symbols with the largest fractional remainder
    const idx = new Int32Array(256);
    for (let i = 0; i < 256; i++) idx[i] = i;
    idx.sort((a, b) => {
      const fa = scaled[a] - Math.floor(scaled[a]);
      const fb = scaled[b] - Math.floor(scaled[b]);
      return fb - fa;
    });
    for (let k = 0; k < leftover; k++) freqs[idx[k]]++;
  } else if (leftover < 0) {
    // scale-down: subtract from largest freqs that are >1
    const idx = new Int32Array(256);
    for (let i = 0; i < 256; i++) idx[i] = i;
    idx.sort((a, b) => freqs[b] - freqs[a]);
    let k = 0;
    while (leftover < 0) {
      if (freqs[idx[k % 256]] > 1) {
        freqs[idx[k % 256]]--;
        leftover++;
      }
      k++;
    }
  }
  return freqs;
}

/** Cumulative frequency table: cum[i] = sum(freqs[0..i]). cum[256] === TOTAL_FREQ. */
function cumulative(freqs: Uint16Array): Uint32Array {
  const cum = new Uint32Array(257);
  for (let i = 0; i < 256; i++) cum[i + 1] = cum[i] + freqs[i];
  return cum;
}

class BitWriter {
  private bytes: number[] = [];
  private cur = 0;
  private nbits = 0;
  writeBit(b: number) {
    this.cur = (this.cur << 1) | (b & 1);
    this.nbits++;
    if (this.nbits === 8) {
      this.bytes.push(this.cur & 0xff);
      this.cur = 0;
      this.nbits = 0;
    }
  }
  finish(): Uint8Array {
    if (this.nbits > 0) {
      this.cur <<= 8 - this.nbits;
      this.bytes.push(this.cur & 0xff);
    }
    return new Uint8Array(this.bytes);
  }
}

class BitReader {
  private idx = 0;
  private cur = 0;
  private nbits = 0;
  constructor(private readonly buf: Uint8Array) {}
  readBit(): number {
    if (this.nbits === 0) {
      this.cur = this.idx < this.buf.length ? this.buf[this.idx++] : 0;
      this.nbits = 8;
    }
    const b = (this.cur >> 7) & 1;
    this.cur = (this.cur << 1) & 0xff;
    this.nbits--;
    return b;
  }
}

export type ProbsCallback = (context: Uint8Array) => Promise<Float32Array>;

/** Encode `bytes`. The callback is invoked once per byte to get the next-byte distribution. */
export async function encode(bytes: Uint8Array, probs: ProbsCallback, contextWindow: number): Promise<Uint8Array> {
  let low = 0;
  let high = MAX_RANGE >>> 0;
  let pending = 0;
  const out = new BitWriter();

  const emit = (b: number) => {
    out.writeBit(b);
    while (pending > 0) {
      out.writeBit(b ^ 1);
      pending--;
    }
  };

  for (let i = 0; i < bytes.length; i++) {
    const ctx = bytes.subarray(Math.max(0, i - contextWindow), i);
    const p = await probs(ctx);
    const freqs = probsToFreqs(p);
    const cum = cumulative(freqs);
    const sym = bytes[i];
    const range = (high - low + 1) >>> 0;
    // Use BigInt for the multiply to avoid overflow in 32-bit space.
    const r = BigInt(range);
    const newHigh = low + Number((r * BigInt(cum[sym + 1])) / BigInt(TOTAL_FREQ)) - 1;
    const newLow = low + Number((r * BigInt(cum[sym])) / BigInt(TOTAL_FREQ));
    low = newLow >>> 0;
    high = newHigh >>> 0;

    while (true) {
      if (high < TOP) {
        emit(0);
      } else if (low >= TOP) {
        emit(1);
        low = (low - TOP) >>> 0;
        high = (high - TOP) >>> 0;
      } else if (low >= SECOND && high < TOP + SECOND) {
        pending++;
        low = (low - SECOND) >>> 0;
        high = (high - SECOND) >>> 0;
      } else {
        break;
      }
      low = (low << 1) >>> 0;
      high = (((high << 1) | 1) >>> 0) & MAX_RANGE;
    }
  }

  pending++;
  if (low < SECOND) emit(0);
  else emit(1);
  return out.finish();
}

/** Decode `compressed` back into `expectedLen` bytes using the same probs callback. */
export async function decode(
  compressed: Uint8Array,
  probs: ProbsCallback,
  expectedLen: number,
  contextWindow: number,
): Promise<Uint8Array> {
  const reader = new BitReader(compressed);
  let low = 0;
  let high = MAX_RANGE >>> 0;
  let code = 0;
  for (let i = 0; i < PRECISION; i++) code = ((code << 1) | reader.readBit()) >>> 0;

  const out = new Uint8Array(expectedLen);
  for (let i = 0; i < expectedLen; i++) {
    const ctx = out.subarray(Math.max(0, i - contextWindow), i);
    const p = await probs(ctx);
    const freqs = probsToFreqs(p);
    const cum = cumulative(freqs);
    const range = (high - low + 1) >>> 0;
    const target = Number(
      ((BigInt(code - low + 1) * BigInt(TOTAL_FREQ) - 1n) / BigInt(range)),
    );
    // Find the symbol whose cumulative interval contains `target`.
    let sym = 0;
    // linear scan is fine at 256 — this is already the slow path
    for (let s = 0; s < 256; s++) {
      if (cum[s + 1] > target) {
        sym = s;
        break;
      }
    }
    out[i] = sym;
    const r = BigInt(range);
    const newHigh = low + Number((r * BigInt(cum[sym + 1])) / BigInt(TOTAL_FREQ)) - 1;
    const newLow = low + Number((r * BigInt(cum[sym])) / BigInt(TOTAL_FREQ));
    low = newLow >>> 0;
    high = newHigh >>> 0;

    while (true) {
      if (high < TOP) {
        // bit 0 confirmed
      } else if (low >= TOP) {
        low = (low - TOP) >>> 0;
        high = (high - TOP) >>> 0;
        code = (code - TOP) >>> 0;
      } else if (low >= SECOND && high < TOP + SECOND) {
        low = (low - SECOND) >>> 0;
        high = (high - SECOND) >>> 0;
        code = (code - SECOND) >>> 0;
      } else {
        break;
      }
      low = (low << 1) >>> 0;
      high = (((high << 1) | 1) >>> 0) & MAX_RANGE;
      code = (((code << 1) | reader.readBit()) >>> 0) & MAX_RANGE;
    }
  }
  return out;
}
